import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Box, Button, Typography } from '@mui/material';
import { ActionItem, ChangeRow } from '../types';
import { ageToken, dedupeChanges, localDay, parseTs } from '../logic';

function Prop({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <Box sx={{ display: 'flex', gap: 1, py: 0.4 }}>
      <Typography variant="caption" sx={{ width: 96, color: 'text.secondary', flexShrink: 0 }}>{label}</Typography>
      <Typography variant="body2" component="div" sx={{ minWidth: 0, overflowWrap: 'anywhere' }}>{children}</Typography>
    </Box>
  );
}

const CHANGES_SHOWN = 5;

const roasText = (value: number | null | undefined): string | null =>
  value != null && Number.isFinite(value) ? `${value.toFixed(1)}x` : null;
export default function FactsRail({ item, now, changes }: { item: ActionItem; now: Date; changes: ChangeRow[] }) {
  const campaign = changes[0]?.campaign_id ?? changes[0]?.unit_label ?? '';
  const [allChanges, setAllChanges] = useState(false);
  // The feed can carry one edit several times over, and five slots spent on three restatements
  // of the same change read as a busy week that never happened.
  const deduped = useMemo(() => dedupeChanges(changes), [changes]);
  const visibleChanges = allChanges ? deduped : deduped.slice(0, CHANGES_SHOWN);
  return (
    <Box sx={{ border: '1px solid #E2E8F0', borderRadius: '12px', p: 2, bgcolor: '#FFFFFF' }}>
      {/* New-customer return is the one figure the chart cannot carry: the collector sends it as
          a single 7-day number, not a series, so there is nothing to draw. It lived on the
          scoreboard the chart replaced, and without a home here it would have disappeared. */}
      {roasText(item.card?.roasStack?.ncRoas7) && (
        <Prop label="New customer">{roasText(item.card?.roasStack?.ncRoas7)} <Typography component="span" variant="caption" sx={{ color: 'text.secondary' }}>(Triple Whale, 7d)</Typography></Prop>
      )}
      {/* Break-even and target are drawn on the chart for enriched cards; repeat them here only
          as a fallback for cards without roasStack, which get no chart reference lines. */}
      {!item.card?.roasStack && item.target_roas != null && <Prop label="Target">{item.target_roas}x <Typography component="span" variant="caption" sx={{ color: 'text.secondary' }}>(Google Ads goal, tax taken out)</Typography></Prop>}
      {!item.card?.roasStack && item.breakeven_roas != null && <Prop label="Breakeven">{item.breakeven_roas}x <Typography component="span" variant="caption" sx={{ color: 'text.secondary' }}>(hand-set, ops-verified)</Typography></Prop>}
      <Prop label="Source">{item.source}</Prop>
      <Prop label="First seen">{localDay(item.first_seen)} ({ageToken(item.first_seen, now)} ago)</Prop>
      {item.due_on && <Prop label="Due">{item.due_on.slice(0, 10)}</Prop>}
      {parseTs(item.surface_on) > now && <Prop label="Surfaces">{item.surface_on.slice(0, 10)}</Prop>}
      {item.claimed_by && <Prop label="Claimed by">{item.claimed_by}{item.claimed_at && ` on ${localDay(item.claimed_at)}`}</Prop>}
      {item.review_criteria && <Prop label="Review">{item.review_criteria}</Prop>}
      {deduped.length > 0 && (
        <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid #E2E8F0' }}>
          <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, mb: 0.5 }}>
            <Typography variant="subtitle2">Changes (30d)</Typography>
            {campaign && <Link to={`/change-log?campaign=${encodeURIComponent(campaign)}`} style={{ fontSize: 12 }}>full history</Link>}
          </Box>
          {visibleChanges.map(c => (
            <Box key={c.change_key} sx={{ display: 'flex', gap: 1, py: 0.25, minWidth: 0 }}>
              <Typography variant="caption" sx={{ color: 'text.secondary', width: 72, flexShrink: 0, fontVariantNumeric: 'tabular-nums' }}>{localDay(c.changed_at)}</Typography>
              <Typography variant="body2" sx={{ minWidth: 0, overflowWrap: 'anywhere' }}>{c.summary}</Typography>
            </Box>
          ))}
          {!allChanges && deduped.length > CHANGES_SHOWN && (
            <Button size="small" sx={{ mt: 0.5, px: 0 }} onClick={() => setAllChanges(true)}>Show all {deduped.length}</Button>
          )}
        </Box>
      )}
    </Box>
  );
}
